import { permitEmployeeDocKeys, getEquipmentDocs } from './wizardHelpers';
import { useState } from 'react';
// Step 5: Özet ve Onay
import { Button } from '../../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../ui/alert-dialog';
import { Card, CardContent } from '../../ui/card';
import { AlertTriangle, CheckCircle, Check, X, Loader2, Clock, Truck, Wrench, Building2 } from 'lucide-react';
import { COMPANY_DOCS } from '../../companies/constants';
import { EMPLOYEE_DOCS } from '../../employees/constants';
import { calculateOverallStatus } from '../../../lib/helpers';
import { INFO_FIELDS, displayValue } from './infoCorrections';

// Tarih formatı: DD/MM/YYYY
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '-';
  // YYYY-MM-DD formatını DD/MM/YYYY'e çevir (timezone sorunu yaşamadan)
  if (typeof dateStr === 'string' && dateStr.includes('-')) {
    const [year, month, day] = dateStr.split('-');
    if (year && month && day) {
      return `${day}/${month}/${year}`;
    }
  }
  // Fallback: Date objesi kullan
  const date = new Date(dateStr);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const PendingCard = ({ title, items, testId }) => (
  <Card className="border-amber-200 bg-amber-50" data-testid={testId}>
    <CardContent className="p-4">
      <h4 className="font-medium text-amber-700 flex items-center gap-2 mb-2"><Clock className="h-4 w-4" />{title}</h4>
      {items.some(it => it.docs) ? (
        <div className="text-sm space-y-3">
          {items.map(it => (
            <div key={it.id} className="border-b border-amber-200 pb-2 last:border-0">
              <div className="font-medium text-amber-700">{it.name}</div>
              <ul className="ml-5 mt-1 space-y-0.5">
                {it.docs.map((d, i) => <li key={i} className="text-amber-600 text-xs">• <span className="font-medium text-slate-700">{d.label}:</span> {d.note}</li>)}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map(it => (
            <span key={it.id} className="inline-flex items-center rounded-full border border-amber-200 bg-white px-2.5 py-0.5 text-xs text-amber-700" data-testid={`${testId}-item-${it.id}`}>
              {it.name}{it.sub && <span className="text-amber-500 ml-1">({it.sub})</span>}
            </span>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

const WizardStep5 = ({
  formData,
  selectedWorkArea,
  locations,
  selectedCompany,
  selectedEmployees,
  selectedEquipments = [],
  saving,
  handleSendDeficiencyReport,
  handleFinalize,
  isFirm = false,
  handleFirmSave,
  equipmentSubtypes = [],
  infoCorrections = {},
  handleInfoError,
  onEditInfo,
  firmInfoBlocked = false,
  equipmentCompany = null,
}) => {
  const infoKeys = Object.keys(infoCorrections);
  const subtypeLabel = (eq) => {
    const key = eq.equipment_subtype || eq.subtype;
    return equipmentSubtypes.find(st => st.subtype_key === key)?.subtype_label || (eq.equipment_type === 'arac' ? 'Araç' : 'İş Ekipmanı');
  };
  const location = locations.find(l => l.id === formData.location_id);
  const docKeys = EMPLOYEE_DOCS.map(d => d.key);
  
  // Talep bitiş tarihi (süresi dolacak belgeler için)
  const permitEndDate = formData.end_date;
  
  // Belge süresi kontrolü - tarihleri UTC olarak karşılaştır
  const checkDocExpiry = (docDate) => {
    if (!docDate || !permitEndDate) return false;
    try {
      // YYYY-MM-DD formatından parse et
      const parseDate = (dateStr) => {
        if (typeof dateStr === 'string' && dateStr.includes('-')) {
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(Date.UTC(year, month - 1, day));
        }
        return new Date(dateStr);
      };
      
      const expiry = parseDate(docDate);
      const end = parseDate(permitEndDate);
      
      // Belge geçerlilik tarihinin son gününe kadar geçerlidir; ancak talep bitişinden önce biterse uyarı
      return expiry < end;
    } catch {
      return false;
    }
  };
  
  // Seçili personelleri onaylı ve eksik olarak ayır
  // Süresi dolacak belgeler de eksik gibi değerlendirilir
  const getPendingDocs = (entity, docs) => docs
    .filter(d => !entity.documents?.[d.key] || entity.documents[d.key] === 'beklemede')
    .map(d => ({ label: d.label, note: entity.document_files?.[d.key] ? 'Onay Bekliyor' : 'Yükleme Bekliyor' }));

  const categorizeEmployees = () => {
    const approved = [];
    const deficient = [];
    const pending = [];
    
    selectedEmployees.forEach(emp => {
      const overall = calculateOverallStatus(emp.documents, permitEmployeeDocKeys(emp, selectedWorkArea, docKeys, formData.selected_optional_employee_docs || []));
      const expiringDocs = [];
      
      // Süresi dolacak belgeleri kontrol et
      EMPLOYEE_DOCS.forEach(doc => {
        const status = emp.documents?.[doc.key];
        // Sadece geçerlilik tarihi olan belgeleri kontrol et (hasValidity: true)
        if (doc.hasValidity) {
          // _validity veya _date alanlarını kontrol et (MYK için her ikisi de olabilir)
          let validityDate = emp.documents?.[`${doc.key}_validity`];
          if (!validityDate && doc.key === 'mesleki_yeterlilik') {
            validityDate = emp.documents?.[`${doc.key}_date`];
          }
          if (status === 'onay' && validityDate && checkDocExpiry(validityDate)) {
            expiringDocs.push({ label: doc.label, date: validityDate });
          }
        }
      });
      
      // Eksik veya süresi dolmuş belgesi olanlar VEYA talep aralığında belgesi dolacak olanlar
      if (overall.status === 'eksik' || overall.status === 'suresi_doldu' || expiringDocs.length > 0) {
        deficient.push({ ...emp, expiringDocs, pendingDocs: getPendingDocs(emp, EMPLOYEE_DOCS) });
      } else if (overall.status === 'onay') {
        approved.push(emp);
      } else {
        pending.push({ ...emp, pendingDocs: getPendingDocs(emp, EMPLOYEE_DOCS) });
      }
    });
    
    return { approved, deficient, pending };
  };
  
  const { approved: approvedEmployees, deficient: deficientEmployees, pending: pendingEmployees } = categorizeEmployees();
  
  // Ekipman kategorileri
  const categorizeEquipments = () => {
    const approved = [];
    const deficient = [];
    const expiring = [];
    const pending = [];
    
    if (!selectedEquipments || selectedEquipments.length === 0) {
      return { approved, deficient, expiring, pending };
    }
    
    selectedEquipments.forEach(eq => {
      const docs = eq.documents || {};
      const eqDefs = getEquipmentDocs(eq.equipment_type).filter(d => !d.optional);
      const docKeys = eqDefs.map(d => d.key);
      const docLabels = Object.fromEntries(eqDefs.map(d => [d.key, d.label]));
      
      const missingDocs = [];
      const expiringDocs = [];
      const pendingDocs = [];
      
      docKeys.forEach(key => {
        const status = docs[key];
        const docDate = docs[`${key}_validity`] || docs[`${key}_date`];
        const deficiencies = docs[`${key}_deficiencies`] || [];
        
        if (!status || status === 'beklemede') {
          pendingDocs.push({ label: docLabels[key], note: eq.document_files?.[key] ? 'Onay Bekliyor' : 'Yükleme Bekliyor' });
        } else if (status === 'eksik') {
          missingDocs.push({ label: docLabels[key], note: deficiencies.length > 0 ? deficiencies.join(', ') : 'Belge eksik' });
        } else if (status === 'onay' && checkDocExpiry(docDate)) {
          expiringDocs.push({ label: docLabels[key], date: docDate });
        }
      });
      
      if (missingDocs.length > 0) {
        deficient.push({ ...eq, missingDocs, expiringDocs, pendingDocs });
      } else if (pendingDocs.length > 0) {
        pending.push({ ...eq, pendingDocs, expiringDocs });
      } else if (expiringDocs.length > 0) {
        expiring.push({ ...eq, expiringDocs });
      } else {
        approved.push(eq);
      }
    });
    
    return { approved, deficient, expiring, pending };
  };
  
  const { approved: approvedEquipments, deficient: deficientEquipments, expiring: expiringEquipments, pending: pendingEquipments } = categorizeEquipments();
  const companyPendingDocs = selectedCompany ? getPendingDocs(selectedCompany, COMPANY_DOCS) : [];
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  
  // Firma eksikliklerini bul
  const companyDeficiencies = [];
  COMPANY_DOCS.forEach(doc => {
    const status = selectedCompany?.documents?.[doc.key];
    const note = selectedCompany?.documents?.[`${doc.key}_note`];
    if (status === 'eksik') {
      companyDeficiencies.push({ label: doc.label, note: note || 'Belge eksik' });
    }
  });
  
  // Personel eksikliklerini bul
  const getEmployeeDeficiencies = (emp) => {
    const deficiencies = [];
    EMPLOYEE_DOCS.forEach(doc => {
      const status = emp.documents?.[doc.key];
      const note = emp.documents?.[`${doc.key}_note`];
      if (status === 'eksik' || status === 'suresi_doldu') {
        deficiencies.push({ 
          label: doc.label, 
          note: note || (status === 'suresi_doldu' ? 'Belge süresi dolmuş' : 'Belge eksik')
        });
      }
    });
    return deficiencies;
  };
  
  // Toplam eksiklik sayısı
  const hasDeficiencies = companyDeficiencies.length > 0 || deficientEmployees.length > 0 || 
    deficientEquipments.length > 0 || expiringEquipments.length > 0;
  const hasOpenIssues = hasDeficiencies || pendingEmployees.length > 0 || pendingEquipments.length > 0 || companyPendingDocs.length > 0;

  const firmView = (company, belongs, tp = '') => {
    const pick = (list) => list.filter(belongs);
    const defs = [];
    COMPANY_DOCS.forEach(doc => {
      if (company?.documents?.[doc.key] === 'eksik') defs.push({ label: doc.label, note: company.documents[`${doc.key}_note`] || 'Belge eksik' });
    });
    return {
      tp, company, companyDeficiencies: defs, companyPendingDocs: company ? getPendingDocs(company, COMPANY_DOCS) : [],
      pendingEmployees: pick(pendingEmployees), approvedEmployees: pick(approvedEmployees), deficientEmployees: pick(deficientEmployees),
      approvedEquipments: pick(approvedEquipments), expiringEquipments: pick(expiringEquipments), pendingEquipments: pick(pendingEquipments), deficientEquipments: pick(deficientEquipments),
    };
  };
  const renderStatusCards = (V) => (
    <>
      {/* Firma Eksiklikleri */}
      {V.companyDeficiencies.length > 0 && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="p-4">
            <h4 className="font-medium text-rose-700 flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4" />
              Firma Belge Eksiklikleri ({V.companyDeficiencies.length})
            </h4>
            <ul className="text-sm space-y-1">
              {V.companyDeficiencies.map((def, idx) => (
                <li key={idx} className="text-rose-600">
                  <X className="h-3 w-3 inline mr-1" />
                  <span className="font-medium">{def.label}:</span> {def.note}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      
      {V.companyPendingDocs.length > 0 && (
        <PendingCard title={`Onay Bekleyen Firma Belgeleri (${V.companyPendingDocs.length})`} testId={`${V.tp}wizard-summary-company-pending`}
          items={[{ id: 'company', name: V.company?.name, docs: V.companyPendingDocs }]} />
      )}

      {V.pendingEmployees.length > 0 && (
        <PendingCard title={`Belgeleri Onay Bekleyen Personeller (${V.pendingEmployees.length})`} testId={`${V.tp}wizard-summary-employee-pending`}
          items={V.pendingEmployees.map(emp => ({ id: emp.id, name: `${emp.name} ${emp.surname}` }))} />
      )}

      {/* Onaylı Personeller */}
      {V.approvedEmployees.length > 0 && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <h4 className="font-medium text-emerald-700 flex items-center gap-2 mb-2">
              <CheckCircle className="h-4 w-4" />
              Onaylı Personeller ({V.approvedEmployees.length})
            </h4>
            <div className="text-sm space-y-1">
              {V.approvedEmployees.map(emp => (
                <div key={emp.id} className="text-emerald-600">
                  <Check className="h-3 w-3 inline mr-1" />
                  {emp.name} {emp.surname}
                  {emp.position && <span className="text-emerald-500 ml-1">({emp.position})</span>}
                  {emp.tc_kimlik_no && <span className="text-emerald-500 ml-2 font-mono text-xs">TC: {emp.tc_kimlik_no}</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Eksik Belgeli Personeller - Süresi dolacak belgeler de dahil */}
      {V.deficientEmployees.length > 0 && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="p-4">
            <h4 className="font-medium text-rose-700 flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4" />
              Eksik / Süresi Dolacak Belgeli Personeller ({V.deficientEmployees.length})
            </h4>
            <div className="text-sm space-y-3">
              {V.deficientEmployees.map(emp => {
                const deficiencies = getEmployeeDeficiencies(emp);
                return (
                  <div key={emp.id} className="border-b border-rose-200 pb-2 last:border-0">
                    <div className="font-medium text-rose-700">
                      <X className="h-3 w-3 inline mr-1" />
                      {emp.name} {emp.surname}
                      {emp.position && <span className="text-rose-500 ml-1">({emp.position})</span>}
                    </div>
                    <ul className="ml-5 mt-1 space-y-0.5">
                      {deficiencies.map((def, idx) => (
                        <li key={idx} className="text-rose-600 text-xs">
                          • <span className="font-medium text-slate-700">{def.label}:</span> {def.note}
                        </li>
                      ))}
                      {emp.pendingDocs?.map((doc, idx) => (
                        <li key={`pend-${idx}`} className="text-amber-600 text-xs">• <span className="font-medium text-slate-700">{doc.label}:</span> {doc.note}</li>
                      ))}
                      {/* Ayrıca süresi dolacak belgeler varsa göster */}
                      {emp.expiringDocs?.map((doc, idx) => (
                        <li key={`exp-${idx}`} className="text-amber-600 text-xs">
                          • {doc.label}: Talep tarih aralığında süresi dolacak. Geçerlilik: {formatDateDisplay(doc.date)}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Onaylı Ekipmanlar */}
      {V.approvedEquipments.length > 0 && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <h4 className="font-medium text-emerald-700 flex items-center gap-2 mb-2">
              <CheckCircle className="h-4 w-4" />
              Onaylı Ekipmanlar ({V.approvedEquipments.length})
            </h4>
            <div className="text-sm space-y-1">
              {V.approvedEquipments.map(eq => (
                <div key={eq.id} className="text-emerald-600 flex items-center gap-2">
                  {eq.equipment_type === 'arac' ? <Truck className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}
                  <Check className="h-3 w-3" />
                  {eq.name}
                  <span className="text-emerald-500 text-xs">({eq.equipment_type === 'arac' ? 'Araç' : 'İş Ekipmanı'})</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Süresi Dolacak Belgeli Ekipmanlar */}
      {V.expiringEquipments.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <h4 className="font-medium text-amber-700 flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4" />
              Talep Aralığında Belgesi Dolacak Ekipmanlar ({V.expiringEquipments.length})
            </h4>
            <div className="text-sm space-y-3">
              {V.expiringEquipments.map(eq => (
                <div key={eq.id} className="border-b border-amber-200 pb-2 last:border-0">
                  <div className="font-medium text-amber-700 flex items-center gap-2">
                    {eq.equipment_type === 'arac' ? <Truck className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}
                    {eq.name}
                    <span className="text-amber-500 text-xs">({eq.equipment_type === 'arac' ? 'Araç' : 'İş Ekipmanı'})</span>
                  </div>
                  <ul className="ml-5 mt-1 space-y-0.5">
                    {eq.expiringDocs.map((doc, idx) => (
                      <li key={idx} className="text-amber-600 text-xs">
                        • {doc.label}: Talep tarih aralığında bu belgenin süresi dolmaktadır. Geçerlilik: {formatDateDisplay(doc.date)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {V.pendingEquipments.length > 0 && (
        <PendingCard title={`Belgeleri Onay Bekleyen Ekipmanlar (${V.pendingEquipments.length})`} testId={`${V.tp}wizard-summary-equipment-pending`}
          items={V.pendingEquipments.map(eq => ({ id: eq.id, name: eq.name, sub: subtypeLabel(eq) }))} />
      )}

      {/* Eksik Belgeli Ekipmanlar */}
      {V.deficientEquipments.length > 0 && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="p-4">
            <h4 className="font-medium text-rose-700 flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4" />
              Eksik Belgeli Ekipmanlar ({V.deficientEquipments.length})
            </h4>
            <div className="text-sm space-y-3">
              {V.deficientEquipments.map(eq => (
                <div key={eq.id} className="border-b border-rose-200 pb-2 last:border-0">
                  <div className="font-medium text-rose-700 flex items-center gap-2">
                    {eq.equipment_type === 'arac' ? <Truck className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}
                    <X className="h-3 w-3" />
                    {eq.name}
                    <span className="text-rose-500 text-xs">({eq.equipment_type === 'arac' ? 'Araç' : 'İş Ekipmanı'})</span>
                  </div>
                  <ul className="ml-5 mt-1 space-y-0.5">
                    {eq.missingDocs.map((doc, idx) => (
                      <li key={idx} className="text-rose-600 text-xs">
                        • <span className="font-medium text-slate-700">{doc.label}:</span> {doc.note}
                      </li>
                    ))}
                    {eq.pendingDocs?.map((doc, idx) => (
                      <li key={`pend-${idx}`} className="text-amber-600 text-xs">• <span className="font-medium text-slate-700">{doc.label}:</span> {doc.note}</li>
                    ))}
                    {/* Ayrıca süresi dolacak belgeler varsa göster */}
                    {eq.expiringDocs?.map((doc, idx) => (
                      <li key={`exp-${idx}`} className="text-amber-600 text-xs">
                        • {doc.label}: Talep tarih aralığında süresi dolacak. Geçerlilik: {formatDateDisplay(doc.date)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
  const eqFirmId = equipmentCompany?.id;
  const firmBanner = (label, name, eq, testId) => (
    <div className={`flex items-center gap-3 rounded-lg px-4 py-3 text-white shadow-sm ${eq ? 'bg-red-600' : 'bg-blue-700'}`} data-testid={testId}>
      <Building2 className="h-5 w-5 shrink-0" />
      <span className="text-sm font-medium opacity-90">{label}:</span>
      <span className="text-base font-bold tracking-wide">{name}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      <h3 className="font-medium">Özet ve Onay</h3>
      
      {/* Genel Bilgiler */}
      <Card>
        <CardContent className="p-4 space-y-3 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-y-2 md:gap-x-20">
            <div><span className="text-slate-500">Lokasyon / İş Tipi / İş Çeşidi:</span> <span className="font-medium ml-1">{[location?.name, formData.is_tipi, formData.is_cesidi].filter(Boolean).join(' / ')}</span></div>
            <div><span className="text-slate-500">Firma:</span> <span className="font-medium ml-1">{selectedCompany?.name}</span>{equipmentCompany && <span className="ml-2 text-xs text-red-600">+ İş Ekipmanı Firması: <b>{equipmentCompany.name}</b></span>}</div>
            <div><span className="text-slate-500">Tarih:</span> <span className="ml-1">{formatDateDisplay(formData.start_date)} - {formatDateDisplay(formData.end_date)}</span></div>
            <div><span className="text-slate-500">Saat:</span> <span className="ml-1">{formData.start_time} - {formData.end_time}</span></div>
            {formData.isg_katip_uzman_name && (
              <div><span className="text-slate-500">İSG Uzman:</span> <span className="ml-1 text-blue-600">{formData.isg_katip_uzman_name}</span></div>
            )}
            {formData.isg_katip_hekim_name && (
              <div><span className="text-slate-500">İSG Hekim:</span> <span className="ml-1 text-emerald-600">{formData.isg_katip_hekim_name}</span></div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {eqFirmId ? (
        <>
          <div className="space-y-3 rounded-xl border-2 border-blue-200 p-3" data-testid="wizard-summary-main-firm">
            {firmBanner('Firma', selectedCompany?.name, false, 'wizard-summary-main-firm-banner')}
            {renderStatusCards(firmView(selectedCompany, x => x.company_id !== eqFirmId))}
          </div>
          <div className="space-y-3 rounded-xl border-2 border-red-200 p-3" data-testid="wizard-summary-eq-firm">
            {firmBanner('İş Ekipmanı Firması', equipmentCompany.name, true, 'wizard-summary-eq-firm-banner')}
            {renderStatusCards(firmView(equipmentCompany, x => x.company_id === eqFirmId, 'eq-'))}
          </div>
        </>
      ) : renderStatusCards(firmView(selectedCompany, () => true))}

      {/* Aksiyon Butonları */}
      {isFirm ? (
        <div className="flex gap-2 justify-center flex-wrap">
          <Button type="button" variant="outline" onClick={() => handleFirmSave(false)} disabled={saving} data-testid="wizard-save-draft-btn">
            Taslak Olarak Kaydet
          </Button>
          <Button type="button" onClick={() => handleFirmSave(true)} disabled={saving || selectedEmployees.length === 0 || firmInfoBlocked} title={firmInfoBlocked ? 'Önce Bölüm 1\'deki önerilen bilgileri girin' : undefined} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wizard-submit-review-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            İSG İncelemesine Gönder
          </Button>
        </div>
      ) : infoKeys.length > 0 ? (
        <Card className="border-rose-300 bg-rose-50" data-testid="wizard-info-error-card">
          <CardContent className="p-4 space-y-3">
            <h4 className="font-semibold text-rose-700 flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Talep Bilgileri Hatalı ({infoKeys.length} alan)</h4>
            <ul className="text-sm space-y-1">
              {infoKeys.map(k => (
                <li key={k} className="flex flex-wrap gap-x-2" data-testid={`wizard-info-error-item-${k}`}>
                  <span className="font-medium text-slate-700">{INFO_FIELDS[k].label}:</span>
                  <span className="text-rose-600 line-through">{displayValue(k, formData, locations)}</span>
                  <span className="text-emerald-700 font-medium">→ {displayValue(k, infoCorrections[k], locations)}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-rose-600">Belgeler incelenmeden talep, önerilen bilgilerle düzeltilmesi için firmaya geri gönderilir ve firmaya e-posta gider.</p>
            <div className="flex gap-2 justify-center flex-wrap">
              <Button type="button" variant="outline" onClick={onEditInfo} data-testid="wizard-info-error-edit-btn">Bölüm 1'e Dön</Button>
              <Button type="button" onClick={handleInfoError} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="wizard-info-error-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />}Talep Bilgileri Hatalı – Firmaya Gönder
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
      <div className="flex gap-2 justify-center flex-wrap">
        {hasDeficiencies && (
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleSendDeficiencyReport} 
            disabled={saving} 
            className="border-rose-600 bg-rose-600 text-white hover:bg-rose-700 hover:text-white"
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            Eksiklik Raporu Gönder (PDF + E-mail)
          </Button>
        )}
        <Button 
          type="button" 
          onClick={() => (hasOpenIssues ? setConfirmFinalize(true) : handleFinalize())} 
          data-testid="wizard-finalize-btn"
          disabled={saving || (approvedEmployees.length === 0 && deficientEmployees.length === 0)} 
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
          Talebi Onayla
        </Button>
      </div>
      )}
      <AlertDialog open={confirmFinalize} onOpenChange={setConfirmFinalize}>
        <AlertDialogContent data-testid="wizard-finalize-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="h-5 w-5" />Eksik belgeler var</AlertDialogTitle>
            <AlertDialogDescription>Talepte eksik belgeler var buna karşı yine de onaylamak istiyor musunuz?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="wizard-finalize-confirm-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setConfirmFinalize(false); handleFinalize(); }} data-testid="wizard-finalize-confirm-ok">Yine de Onayla</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WizardStep5;
